import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { API, authFetch } from '../utils/api';
import { getSocket } from '../utils/socket';
import { createChannel as createPresenceChannel } from '../utils/settlementPresence';
// 표시 헬퍼는 프레즌스 3종 공용 — 정본은 utils/carPresence.js.
import { mySid as presenceMySid, presenceOthersLabeled } from '../utils/carPresence';
import CancelAgencyMalsoReview from './CancelAgencyMalsoReview';
import PresenceDot from './PresenceDot';
import PresenceBanner from './PresenceBanner';

// ═══ 말소증 보관함 — 3-pane 검수대 (리디자인 2026-08-25) ═══
// 정본: erp/app/docs/malso-scan-intake/plan.md §5(폴더 메타포)·§5.5(서버 계약).
// 서버 실물: routes/cancelAgency.js 의 malso-scan / assign-client / malso-box(+items) / car-no,
//           조립은 utils/malsoBox.js (폴더 = 정산 귀속처, 진실은 DB clientId — 파일은 옮기지 않는다).
//
// 화면 = **한 화면 안 3 pane** (직전 '폴더목록 → 폴더상세' 2단 내비게이션을 대체).
//   좌 190px 폴더 레일 : 월 스위처 + 미분류(앰버·기본 선택) + 거래처 폴더 + [등록]
//   중 목록            : 고밀도 행 (차량번호 + 차명·구청·소유자 + 뱃지 + 금액), 다중선택 툴바
//   우 ~45% 검수 패널  : 선택 행의 원본 + 필드 편집 (CancelAgencyMalsoReview)
// 반응형 3단 (window.innerWidth):
//   ≥1280 3-pane · 768~1279 레일→상단 칩 줄 + 2-pane · <768 스택 + 행 탭 시 풀스크린 검수
//
// 계약 세 가지 (서버 주석과 대칭):
//   1) unassigned 폴더 조회는 month 파라미터를 **보내지 않는다** — 미분류는 월 장부가 아니라 작업 큐고,
//      홈 배지(월 무관 총 잔여)와 행 수가 어긋나면 안 된다.
//   2) POST /settlement-items/assign-client 는 STAFF_ALLOW 미등재 = 직원 403.
//      귀속 확정은 요율 계산 + 원장 청구 전기를 동반하는 경영 행위라 UI 에서도 버튼을 숨긴다.
//      (2026-08-25 확장) 이미 귀속된 행의 **재지정**도 같은 라우트 — 옛 거래처 −prior / 새 거래처 +new.
//   3) 필드 저장은 라우트가 둘로 갈린다 (차량번호=재대조 / 나머지=전체 수정+요율 재계산) —
//      CancelAgencyMalsoReview 헤더 주석 참조.
//   4) (2026-08-25 OCR 큐) 등록은 **업로드와 인식이 분리**된다.
//      POST /cancel-agency/malso-upload (파일 1개) → {success, itemId, item} · item.ocrStatus='pending'.
//      인식은 서버 큐가 뒤에서 돌고 결과는 소켓 `malso:ocr` 로 온다
//        {itemId, status, kind:'request'|'unassigned'|'duplicated'|'failed', item?, replacedBy?, requestId?, dupOfId?}
//      → 업로드 중에만 이탈 경고. 업로드가 끝나면 서버가 알아서 진행하므로 경고를 걷고
//        '신규 등록' 폴더의 자리 행이 소켓 수신에 맞춰 제자리에서 치환된다.
//      소켓이 없으면(토큰 없음/미연결) recent 폴더에 자리 행이 보이는 동안만 10초 폴링으로 대체.
function CancelAgencyMalsoBox({ showToast, user, agencies, isCancelAgency }) {
  var tier = useWidthTier();                       // 'desktop' | 'tablet' | 'mobile'
  var isMobile = tier === 'mobile';
  var paneRef = useRef(null);                      // 3-pane 그리드 — 남은 화면 높이를 재는 기준점
  var isStaff = !!(isCancelAgency && user && user.agency_staff); // 직원 = 조회/스캔만 (지정 불가)
  var fmt = function(n) { return (parseInt(n, 10) || 0).toLocaleString(); };
  var toast = function(m, t) { if (showToast) showToast(m, t); };

  // #cap-web 정산: 동시 작업 프레즌스 — {itemId: users[]}. 카드 배지 전용(엔터/리브는 검수 패널이 한다).
  // users 는 **접속(소켓) 단위** — 본인 접속(sid)만 빼고 같은 계정의 다른 접속은 기기 표기를 붙여 남긴다.
  var [presenceByItem, setPresenceByItem] = useState({});
  // 구독 + 목록 요청을 한 채널로 묶는다 — CancelAgencySettlement(항상 마운트)도 같은 소켓 싱글턴
  // (settlement:presence)을 구독하므로, 스냅샷을 "이 채널이 요청한 id" 로 거르는 주체가 팩토리다.
  var presenceChanRef = useRef(null);
  if (!presenceChanRef.current) presenceChanRef.current = createPresenceChannel();
  var presenceChan = presenceChanRef.current;
  var currentUserId = user && user.id;
  // 내 socket.id 는 렌더당 1회만 읽는다 — 행마다 부르면 목록 길이만큼 getSocket() 이 불린다.
  var mySid = presenceMySid();
  var othersForItem = function(itemId) {
    return presenceOthersLabeled(presenceByItem[itemId] || [], mySid, currentUserId);
  };
  useEffect(function() {
    var unsub = presenceChan.subscribe(function(evt) {
      if (evt.type === 'presence') {
        var p = evt.payload || {};
        if (!p.itemId) return;
        setPresenceByItem(function(prev) { var n = Object.assign({}, prev); n[p.itemId] = p.users || []; return n; });
      } else if (evt.type === 'snapshot') {
        // 팩토리가 이 채널이 요청한 id 로 걸러 넘긴다 — 담겨 온 것만 반영하고, 값이 비었으면
        // "이제 아무도 없다"로 지운다 (프레즌스 3종 4화면 공통 코드).
        var snap = evt.payload || {};
        setPresenceByItem(function(prev) {
          var n = Object.assign({}, prev);
          Object.keys(snap).forEach(function(id) { if (snap[id] && snap[id].length) n[id] = snap[id]; else delete n[id]; });
          return n;
        });
      }
    });
    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  var itemPresenceBadge = function(itemId) {
    return <PresenceDot users={othersForItem(itemId)} reserve />;
  };
  // 검수 패널 상단 배너 (마크업은 components/PresenceBanner.js 한 벌)
  var itemPresenceBanner = function(itemId) {
    return <PresenceBanner users={othersForItem(itemId)} target="이 항목" />;
  };

  // admin/doc_agency 는 대행사를 골라야 조회된다 (cancel_agency 는 서버가 본인 agencyId 로 override).
  var [agencyId, setAgencyId] = useState('');
  var canQuery = function() { return isCancelAgency || !!agencyId; };
  var amp = function() { return (!isCancelAgency && agencyId) ? ('&agencyId=' + agencyId) : ''; };
  var scopeBody = function(o) { if (!isCancelAgency && agencyId) o.agencyId = parseInt(agencyId, 10); return o; };

  var [month, setMonth] = useState(kstMonth());
  var [summary, setSummary] = useState(null);        // {unassigned:{count,needsReview}, recent:{count}, folders:[], kpi:{}}
  var [folder, setFolder] = useState(UNFILED);       // 기본 선택 = 미분류 (작업 큐가 홈이다)
  var [items, setItems] = useState([]);
  var [loading, setLoading] = useState(false);
  var [selected, setSelected] = useState([]);        // 미분류 뷰 다중 선택
  var [selId, setSelId] = useState(0);               // 검수 패널 대상 행
  var [clients, setClients] = useState([]);          // 거래처 드롭다운 (직원은 /agency-clients 403 → 생략)
  // 작업 유형 프리셋 — 정산탭(CancelAgencySettlement) 수기 추가/수정 모달과 **같은 경로·같은 의미**.
  // 검수 패널의 '등록 대기' 초안에서 금액을 채우는 **입력 보조**일 뿐이라 payload 에는 나가지 않는다
  // (assign-client 는 fees 5항목 + issueTaxInvoice 만 받는다). 실패하면 [] → 셀렉트 자체가 숨는다.
  var [workTypes, setWorkTypes] = useState([]);
  var [assignOpen, setAssignOpen] = useState(false);
  var [assignIds, setAssignIds] = useState([]);      // 일괄(체크) 또는 검수 패널 단건
  var [railQ, setRailQ] = useState('');              // 좌측 거래처 폴더 검색 (Jin 8/25)
  var [railSort, setRailSort] = useState('recent');  // 폴더 정렬: recent(최근 유입)|name(이름)|count(등록 수) (Jin 8/26)
  var [assignClientId, setAssignClientId] = useState('');   // 다건 확인 줄에서 고른 거래처
  var [assignQ, setAssignQ] = useState('');                 // 지정 팝업 안 거래처 검색
  var [assignMode, setAssignMode] = useState('new');  // 'new' 지정 | 'change' 재지정(이미 귀속된 행 포함)
  // 단건 지정은 팝업에서 끝나지 않는다 — 검수 패널이 '등록 대기' 로 바뀌어 수수료를 확인/수정하고 [등록].
  // { itemId, clientId, clientName, issueTaxInvoice, draft:{...5항목}, priorClientName, priorSubtotal }
  var [pending, setPending] = useState(null);
  var [carNoEdit, setCarNoEdit] = useState(null);    // {id, value} — 목록의 빠른 [차량번호 입력]
  // 수동 요청 연결 — 미분류 행을 진행 중 요청에서 직접 골라 잇는다 (OCR 오인식 구제).
  var [linkItem, setLinkItem] = useState(null);      // 연결 대상 미분류 행 (null = 시트 닫힘)
  var [linkReqs, setLinkReqs] = useState([]);        // 연결 가능한 요청 (접수/진행중/수거완료)
  var [linkQ, setLinkQ] = useState('');
  var [linkLoading, setLinkLoading] = useState(false);
  // 차량번호 검색 — 서버 검색 API 가 없어 폴더 items 를 훑어 클라에서 거른다 (월 스코프 명시).
  var [q, setQ] = useState('');
  var [hits, setHits] = useState(null);              // null = 검색 중 아님
  var [searching, setSearching] = useState(false);
  // 등록 — 모달은 파일 고르기 전용이고(시작하면 즉시 닫힌다), 진행/결과는 화면 상단이 담당한다 (Jin 8/25).
  // 거래처 사전 선택은 없앴다 — 항상 clientId 미전송(서버가 요청 매칭 or 미분류)이고,
  // 귀속은 등록 후 검수 패널/일괄 지정에서 한다.
  // OCR 큐 전환 후 '진행' 은 두 구간이다: **업로드**(클라가 붙잡고 있음) → **인식**(서버 큐).
  var [scanOpen, setScanOpen] = useState(false);
  var [scanFiles, setScanFiles] = useState([]);
  var [scanRunning, setScanRunning] = useState(false); // 업로드 구간만 true (인식은 서버가 진행)
  var [upDone, setUpDone] = useState(0);               // 업로드 완료 파일 수 (진행바 분자)
  var [ocrRows, setOcrRows] = useState([]);            // [{key,kind}] — 요약 칩의 모수 (소켓 수신 누적)
  var [scanStopped, setScanStopped] = useState(0);     // [등록 취소] 로 건너뛴 남은 파일 수
  var cancelRef = useRef(false);                       // 취소 신호 — 순차 루프가 다음 파일 전에 읽는다
  var [dragOver, setDragOver] = useState(false);       // 화면 전역 드래그 오버 하이라이트

  // 새로고침/탭 닫기 가드 — **업로드 중에만**. 인식은 서버 큐가 계속 진행하므로
  // 업로드가 끝난 뒤엔 이탈해도 잃는 것이 없다 (경고를 남기면 거짓말이 된다).
  useEffect(function() {
    if (!scanRunning) return undefined;
    var warn = function(e) { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return function() { window.removeEventListener('beforeunload', warn); };
  }, [scanRunning]);

  // 서버 미배포/에러 응답을 사람 말로 (메시지 > error > 폴백)
  var describeError = function(d, fallback) {
    return (d && (d.message || d.error)) || fallback;
  };

  // 패널 높이 — 남은 화면을 채운다 (재는 effect 는 scanActive 계산 뒤에 있다)
  var [paneH, setPaneH] = useState(PANEL_MIN);

  // 폴더 정렬 — 서버는 lastDate desc(최근순)로 준다. 이름/건수는 클라에서.
  var sortFolders = function(list) {
    var a = list.slice();
    if (railSort === 'name') a.sort(function(x, y) { return String(x.name || '').localeCompare(String(y.name || ''), 'ko'); });
    else if (railSort === 'count') a.sort(function(x, y) { return (y.monthCount || 0) - (x.monthCount || 0) || String(x.name || '').localeCompare(String(y.name || ''), 'ko'); });
    return a;                                        // recent = 서버 순서 유지
  };
  var RAIL_SORTS = [['recent', '최근순'], ['name', '이름순'], ['count', '건수순']];

  var isUnassigned = !!folder && folder.key === 'unassigned';
  var isRecent = !!folder && folder.key === 'recent';
  var rows = hits !== null ? hits : items;
  var rowIds = rows.map(function(r) { return r.id; });
  var selIndex = rowIds.indexOf(selId);
  var current = selIndex >= 0 ? rows[selIndex] : null;

  // 드래그 앤 드랍 — 떨어뜨린 파일을 등록 모달에 채워서 연다 (파일 확인 후 [등록 시작]).
  var ACCEPT_RE = /\.(jpe?g|png|webp|heic|heif|pdf)$/i;
  var onBannerDrop = function(e) {
    e.preventDefault(); setDragOver(false);
    if (scanRunning) { toast('업로드가 진행 중입니다 — 완료 후 추가 등록할 수 있습니다', 'err'); return; }
    var all = Array.prototype.slice.call((e.dataTransfer && e.dataTransfer.files) || []);
    var files = all.filter(function(f) { return ACCEPT_RE.test(f.name || '') || /^image\//.test(f.type) || f.type === 'application/pdf'; });
    if (!files.length) { toast('이미지 또는 PDF 파일만 올릴 수 있습니다', 'err'); return; }
    if (files.length < all.length) toast((all.length - files.length) + '개 파일은 형식이 맞지 않아 제외했습니다', 'err');
    setScanFiles(files); setOcrRows([]); setScanOpen(true);
  };

  // ── 로드 ────────────────────────────────────────────────────────────────
  var loadBox = function() {
    if (!canQuery()) { setSummary(null); return; }
    authFetch(API + '/cancel-agency/malso-box?month=' + month + amp())
      .then(function(r) { return r.json(); })
      .then(function(d) {
        setSummary({
          unassigned: (d && d.unassigned) || { count: 0, needsReview: 0 },
          // recent.pending = 인식 대기 건수 (서버 계약 · 미배포 서버는 미포함 → 0)
          recent: (d && d.recent) || { count: 0, pending: 0 },
          folders: (d && Array.isArray(d.folders)) ? d.folders : [],
          kpi: (d && d.kpi) || { monthTotal: 0, monthRequest: 0 }
        });
      })
      .catch(function() { setSummary({ unassigned: { count: 0, needsReview: 0 }, recent: { count: 0 }, folders: [], kpi: { monthTotal: 0, monthRequest: 0 } }); });
  };
  // unassigned 와 recent 는 month 를 붙이지 않는다 (계약 — 위 헤더 주석 1).
  //   미분류 = 월 무관 작업 큐 / 신규 등록 = 오늘 올린 것 (말소일이 지난달이어도 보여야 한다).
  var MONTHLESS = { unassigned: 1, recent: 1 };
  var itemsUrl = function(key) {
    return API + '/cancel-agency/malso-box/items?folder=' + encodeURIComponent(key)
      + (MONTHLESS[key] ? '' : ('&month=' + month)) + amp();
  };
  var loadItems = function(key) {
    if (!key) return;
    setLoading(true);
    authFetch(itemsUrl(key))
      .then(function(r) { return r.json(); })
      .then(function(d) { setItems((d && Array.isArray(d.items)) ? d.items : []); setLoading(false); })
      .catch(function() { setItems([]); setLoading(false); });
  };
  var reload = function() { loadBox(); if (folder) loadItems(folder.key); };
  useEffect(function() { loadBox(); }, [month, agencyId, isCancelAgency]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(function() {
    if (!canQuery() || isStaff) { setClients([]); return; }
    authFetch(API + '/agency-clients' + ((!isCancelAgency && agencyId) ? ('?agencyId=' + agencyId) : ''))
      .then(function(r) { return r.json(); })
      .then(function(d) { setClients(Array.isArray(d) ? d : []); })
      .catch(function() { setClients([]); });
  }, [agencyId, isCancelAgency, isStaff]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(function() {
    if (!canQuery() || isStaff) { setWorkTypes([]); return; }
    authFetch(API + '/agency-work-types' + ((!isCancelAgency && agencyId) ? ('?agencyId=' + agencyId) : ''))
      .then(function(r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
      .then(function(d) { setWorkTypes(Array.isArray(d) ? d : []); })
      .catch(function() { setWorkTypes([]); });
  }, [agencyId, isCancelAgency, isStaff]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(function() {                                    // 폴더 상세는 월 전환 시 함께 갱신
    if (folder) { setSelected([]); loadItems(folder.key); }
  }, [folder, month]); // eslint-disable-line react-hooks/exhaustive-deps

  // Shift 범위 선택의 기준점(마지막으로 찍은 행 인덱스). 목록이 바뀌면 인덱스 의미가 사라져 리셋.
  var anchorRef = useRef(-1);

  // 검수 패널 대상 유지 — 목록이 바뀌면 사라진 행 대신 첫 행으로. 모바일은 탭해야 열린다.
  var rowKey = rowIds.join(',');
  useEffect(function() { anchorRef.current = -1; }, [rowKey]);
  // 목록이 바뀔 때마다(로드/재조회 포함) 그 행들의 프레즌스를 요청 — 배지가 새 목록을 따라간다.
  useEffect(function() {
    // 목록에서 빠진 id 정리는 팩토리 몫 — requestList 가 직전 요청과 비교해 빈 users 로 통지한다.
    presenceChan.requestList(rowIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowKey]);
  useEffect(function() {
    if (isMobile) return;
    if (!rows.length) { if (selId) setSelId(0); return; }
    if (rowIds.indexOf(selId) < 0) setSelId(rows[0].id);
  }, [rowKey, isMobile]); // eslint-disable-line react-hooks/exhaustive-deps

  // 등록 대기 중 다른 행으로 가면 대기를 버린다 (서버 무호출) — 상태 꼬임 방지.
  useEffect(function() {
    if (pending && pending.itemId !== selId) setPending(null);
  }, [selId]); // eslint-disable-line react-hooks/exhaustive-deps

  var openFolder = function(key, name) { setHits(null); setQ(''); setSelId(0); setFolder({ key: key, name: name }); };

  // ── 말소증 뷰어 (라이트박스) — 목록 [보기] / 검수 패널 [⤢ 원본]. 인증 첨부라 blob 수신 후 objectURL.
  //    (<a href> 는 JWT 미탑재라 401 — CancelAgency.openPdf 관례의 인앱 판)
  var [viewer, setViewer] = useState(null); // {item, url, isPdf, loading}
  var openDoc = function(it) {
    setViewer({ item: it, url: '', isPdf: false, loading: true });
    authFetch(API + '/settlement-items/' + it.id + '/malso-doc')
      .then(function(r) { if (!r.ok) throw new Error('fail'); return r.blob(); })
      .then(function(blob) {
        var u = URL.createObjectURL(blob);
        setViewer(function(v) { return (v && v.item.id === it.id) ? { item: it, url: u, isPdf: blob.type === 'application/pdf', loading: false } : v; });
      })
      .catch(function() { setViewer(null); toast('말소증 원본을 열 수 없습니다', 'err'); });
  };
  var closeViewer = function() {
    setViewer(function(v) { if (v && v.url) setTimeout(function() { URL.revokeObjectURL(v.url); }, 1000); return null; });
  };
  var viewerNewTab = function() {
    if (!viewer || !viewer.url) return;
    var w = window.open(viewer.url, '_blank');
    if (!w) toast('팝업이 차단되어 새 탭을 열 수 없습니다', 'err');
  };

  // ── 차량번호 검색 (전 폴더 횡단) ────────────────────────────────────────
  // 서버 검색 엔드포인트가 없으므로 이달 폴더 + 미분류를 5개씩 나눠 훑는다 (명시 액션에서만 실행).
  var runSearch = function() {
    var key = String(q || '').replace(/\s/g, '');
    if (!key) { setHits(null); return; }
    if (!summary) return;
    var keys = ['unassigned'].concat(summary.folders.map(function(f) { return f.key; }));
    var nameOf = {}; summary.folders.forEach(function(f) { nameOf[f.key] = f.name; });
    nameOf['unassigned'] = '미분류';
    setSearching(true); setHits([]); setSelId(0);
    var acc = [];
    var chunk = function(i) {
      if (i >= keys.length) {
        setHits(acc.slice()); setSearching(false);
        return;
      }
      var part = keys.slice(i, i + 5);
      Promise.all(part.map(function(k) {
        return authFetch(itemsUrl(k)).then(function(r) { return r.json(); })
          .then(function(d) { return { key: k, items: (d && Array.isArray(d.items)) ? d.items : [] }; })
          .catch(function() { return { key: k, items: [] }; });
      })).then(function(res) {
        res.forEach(function(g) {
          g.items.forEach(function(it) {
            if (String(it.carNo || '').replace(/\s/g, '').indexOf(key) >= 0) {
              acc.push(Object.assign({}, it, { folderKey: g.key, folderName: nameOf[g.key] || g.key }));
            }
          });
        });
        chunk(i + 5);
      });
    };
    chunk(0);
  };
  var clearSearch = function() { setHits(null); setQ(''); setSelId(0); };

  // ── 거래처 지정 / 재지정 ────────────────────────────────────────────────
  // 팝업은 **거래처 고르기까지만** 한다 (Jin 8/25). 그 다음이 갈린다:
  //   다건(체크 일괄) — "N건을 {거래처}로 지정합니다" 한 줄 확인 → [지정] → 거래처 요율로 즉시 지정.
  //   단건(검수 패널 발) — 팝업이 닫히고 패널이 '등록 대기' 로 바뀐다. dryRun 결과를 초안으로 깔아
  //     수수료를 눈으로 보고 고친 뒤 [등록]. 고쳤으면 fees 를 동봉한다(서버가 computeManualItemized).
  // 이미 귀속된 행도 대상 — 서버가 옛 거래처 −prior / 새 거래처 +new 로 다시 전기한다.
  var openAssign = function(ids) {
    var byId = {};
    rows.forEach(function(r) { byId[r.id] = r; });
    var change = ids.some(function(id) { return byId[id] && (parseInt(byId[id].clientId, 10) || 0) > 0; });
    setAssignIds(ids); setAssignClientId(''); setAssignQ('');
    setAssignMode(change ? 'change' : 'new'); setAssignOpen(true);
  };
  // 단건 — dryRun 으로 초안 금액을 받아 패널을 '등록 대기' 로 넘긴다 (여기서는 아무것도 쓰지 않는다).
  var startPending = function(client) {
    var id = assignIds[0];
    var row = rows.filter(function(r) { return r.id === id; })[0];
    authFetch(API + '/settlement-items/assign-client', {
      method: 'POST',
      body: JSON.stringify(scopeBody({ ids: [id], clientId: client.id, dryRun: true }))
    }).then(function(r) { return r.json(); }).then(function(d) {
      var pv = (d && d.success && d.dryRun && (d.preview || [])[0]) || null;
      if (!pv) {
        var why = (d && (d.skipped || [])[0] && d.skipped[0].reason) || '금액을 계산할 수 없습니다';
        toast(why, 'err');
        return;
      }
      setAssignOpen(false); setAssignIds([]); setAssignClientId('');
      setPending({
        itemId: id, clientId: client.id, clientName: d.clientName || client.name,
        issueTaxInvoice: d.issueTaxInvoice ? 1 : 0,
        priorClientName: pv.priorClientName || '', priorSubtotal: parseInt(pv.priorSubtotal, 10) || 0,
        // 재교부 3분할(M1) 이후 computeSettlement 는 재교부 금액을 reissueStampDuty/reissueServiceFee 로 낸다
        // — 구 reissueFee 만 읽으면 초안이 늘 0 이라 재교부 건이 무료로 보인다.
        draft: {
          registrationTax: parseInt(pv.registrationTax, 10) || 0, stampDuty: parseInt(pv.stampDuty, 10) || 0,
          wonbuFee: parseInt(pv.wonbuFee, 10) || 0, serviceFee: parseInt(pv.serviceFee, 10) || 0,
          reissueFee: parseInt(pv.reissueFee, 10) || 0,
          reissueStampDuty: parseInt(pv.reissueStampDuty, 10) || 0,
          reissueServiceFee: parseInt(pv.reissueServiceFee, 10) || 0,
          otherTaxableFee: parseInt(pv.otherTaxableFee, 10) || 0
        }
      });
      if (row) setSelId(row.id);
    }).catch(function() { toast('금액을 계산할 수 없습니다', 'err'); });
  };
  var pickAssignClient = function(client) {
    if (assignIds.length > 1) { setAssignClientId(String(client.id)); return; }   // 다건 = 확인 줄
    startPending(client);
  };
  // 다건 — 거래처 요율로 즉시 지정 (금액 수정 없음)
  var doAssign = function() {
    var cid = parseInt(assignClientId, 10) || 0;
    if (!cid || assignIds.length === 0) { toast('거래처와 항목을 선택하세요', 'err'); return; }
    authFetch(API + '/settlement-items/assign-client', {
      method: 'POST',
      body: JSON.stringify(scopeBody({ ids: assignIds, clientId: cid }))
    }).then(function(r) { return r.json(); }).then(function(d) {
      if (!d || !d.success) { toast((d && d.error) || '귀속 실패', 'err'); return; }
      var skipped = (d.skipped || []).length;
      toast((assignMode === 'change' ? '거래처 변경 ' : '거래처 귀속 ') + (d.updated || 0) + '건 완료'
        + (skipped ? (' · ' + skipped + '건 건너뜀') : ''));
      setAssignOpen(false); setAssignClientId(''); setAssignIds([]); setSelected([]);
      reload();
    }).catch(function() { toast('귀속 실패', 'err'); });
  };
  // 패널 [등록] — fees 는 사용자가 초안을 고쳤을 때만 싣는다 (안 고쳤으면 서버가 거래처 요율로 계산).
  var submitPending = function(fees) {
    if (!pending) return;
    var body = scopeBody({ ids: [pending.itemId], clientId: pending.clientId });
    if (fees) body.fees = fees;
    authFetch(API + '/settlement-items/assign-client', { method: 'POST', body: JSON.stringify(body) })
      .then(function(r) { return r.json(); }).then(function(d) {
        if (!d || !d.success) { toast((d && d.error) || '등록 실패', 'err'); return; }
        if (!d.updated) { toast(((d.skipped || [])[0] || {}).reason || '등록되지 않았습니다', 'err'); return; }
        toast(pending.priorClientName ? '거래처를 변경했습니다' : '거래처에 등록했습니다');
        setPending(null);
        reload();
      }).catch(function() { toast('등록 실패', 'err'); });
  };

  // ── 수기 차량번호 → 요청 재대조 (목록의 빠른 액션) ──────────────────────
  var saveCarNo = function() {
    if (!carNoEdit) return;
    var v = String(carNoEdit.value || '').trim();
    if (!v) { toast('차량번호를 입력하세요', 'err'); return; }
    authFetch(API + '/settlement-items/' + carNoEdit.id + '/car-no', {
      method: 'PUT', body: JSON.stringify({ carNo: v })
    }).then(function(r) { return r.json(); }).then(function(d) {
      if (!d || !d.success) { toast((d && d.error) || '저장 실패', 'err'); return; }
      toast(d.kind === 'request' ? '요청 건으로 자동 완료됐습니다' : '차량번호를 저장했습니다');
      setCarNoEdit(null);
      reload();
    }).catch(function() { toast('저장 실패', 'err'); });
  };

  // ── 행 삭제 (잘못 올린 파일 정리 — 대량말소증 통합으로 미분류가 오업로드 수납처가 됨) ──
  // 정산탭 관례(deleteEditItem)와 동일 API.
  var deleteItem = function(it) {
    if (!window.confirm('이 말소증 항목을 삭제할까요?\n' + (it.carNo || '(차량번호 없음)') + ' / ' + (it.date || ''))) return;
    authFetch(API + '/settlement-items/' + it.id, { method: 'DELETE' })
      .then(function(r) { return r.json(); }).then(function(d) {
        if (!d || !d.success) { toast((d && d.error) || '삭제 실패', 'err'); return; }
        toast('삭제 완료');
        setSelected(function(prev) { return prev.filter(function(x) { return x !== it.id; }); });
        // 다음 행으로 이동 — 검수 흐름이 끊기지 않게 (없으면 이전 행)
        var i = rowIds.indexOf(it.id);
        setSelId(rowIds[i + 1] || rowIds[i - 1] || 0);
        reload();
      }).catch(function() { toast('삭제 실패', 'err'); });
  };
  // 일괄 삭제 — 서버에 배치 DELETE 가 없어 순차 호출 (스캔과 같은 이유로 동시 호출하지 않는다).
  var deleteSelected = function() {
    if (!selected.length) return;
    if (!window.confirm('선택한 ' + selected.length + '건을 삭제할까요? 되돌릴 수 없습니다.')) return;
    var ids = selected.slice();
    var step = function(i, ok) {
      if (i >= ids.length) {
        toast('삭제 ' + ok + '건 완료' + (ok < ids.length ? (' · ' + (ids.length - ok) + '건 실패') : ''));
        setSelected([]); setSelId(0); reload();
        return;
      }
      authFetch(API + '/settlement-items/' + ids[i], { method: 'DELETE' })
        .then(function(r) { return r.json(); })
        .then(function(d) { step(i + 1, ok + ((d && d.success) ? 1 : 0)); })
        .catch(function() { step(i + 1, ok); });
    };
    step(0, 0);
  };

  // ── 키보드 — ↑↓ 행 이동 / Del 삭제. 입력 포커스·모달 중엔 무시. ─────────
  useEffect(function() {
    if (isMobile) return undefined;
    var onKey = function(e) {
      if (scanOpen || assignOpen || carNoEdit || viewer || linkItem) return;
      var t = e.target;
      var tag = t && t.tagName;
      if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA' || (t && t.isContentEditable)) return;
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        if (!rowIds.length) return;
        e.preventDefault();
        var i = rowIds.indexOf(selId);
        var next = e.key === 'ArrowDown'
          ? rowIds[Math.min(rowIds.length - 1, i < 0 ? 0 : i + 1)]
          : rowIds[Math.max(0, i < 0 ? 0 : i - 1)];
        setSelId(next);
      } else if (e.key === 'Delete') {
        if (isStaff || !current) return;
        e.preventDefault();
        deleteItem(current);
      }
    };
    document.addEventListener('keydown', onKey);
    return function() { document.removeEventListener('keydown', onKey); };
  }); // 의존성 전체 — 핸들러가 rows/selId/모달 상태를 모두 읽는다 (매 렌더 재바인딩, 리스너 1개 유지)

  // ── 업로드 (순차 1건씩 · OCR 없음) ─────────────────────────────────────
  // 장당 1초 미만 — 진행바는 "업로드 n/N" 이고, 인식 결과는 여기서 알 수 없다(소켓이 나중에 준다).
  // 끝나면 '신규 등록' 폴더로 자동 전환해 방금 올린 자리 행이 바로 보이게 한다.
  var uploadStep = function(idx, files, ok, acc) {
    if (idx >= files.length || cancelRef.current) {
      setScanRunning(false);
      setScanStopped(cancelRef.current ? (files.length - idx) : 0);
      loadBox();
      openFolder(RECENT.key, RECENT.name);
      if (ok > 0) toast(ok + '장 업로드 완료 — 인식은 자동으로 진행됩니다');
      return;
    }
    var fd = new FormData();
    fd.append('file', files[idx]);
    // clientId 는 보내지 않는다 — 등록 시점에 거래처를 고르지 않는 플로우 (Jin 8/25).
    if (!isCancelAgency && agencyId) fd.append('agencyId', String(agencyId));
    var failStep = function(msg) {
      acc.push({ key: 'up' + idx, kind: 'failed' });
      setOcrRows(acc.slice());
      if (msg) toast(msg, 'err');
      uploadStep(idx + 1, files, ok, acc);
    };
    authFetch(API + '/cancel-agency/malso-upload', { method: 'POST', body: fd })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }, function() { return { ok: false, d: null }; }); })
      .then(function(o) {
        var d = o.d || {};
        if (!o.ok || !d.success) { failStep(idx === 0 ? describeError(d, '업로드에 실패했습니다') : ''); return; }
        setUpDone(idx + 1);
        uploadStep(idx + 1, files, ok + 1, acc);
      })
      .catch(function() { failStep(idx === 0 ? '업로드에 실패했습니다' : ''); });
  };
  // 업로드 중 재진입 차단 — 순차 업로드 파이프가 하나라 동시 세션 금지.
  var openScan = function() {
    if (scanRunning) { toast('업로드가 진행 중입니다 — 완료 후 추가 등록할 수 있습니다', 'err'); return; }
    setScanFiles([]); setScanOpen(true);
  };
  // 시작하면 모달은 즉시 닫힌다 — 진행/결과는 상단 스트립이 맡는다.
  var startScan = function() {
    if (!scanFiles.length) { toast('말소증 파일을 선택하세요', 'err'); return; }
    cancelRef.current = false;
    setScanOpen(false); setScanRunning(true); setUpDone(0); setOcrRows([]); setScanStopped(0);
    uploadStep(0, scanFiles, 0, []);
  };
  var closeScan = function() {
    if (scanRunning) return;
    setScanOpen(false); setScanFiles([]);
  };
  var cancelScan = function() {
    if (!scanRunning) return;
    cancelRef.current = true;
    toast('업로드를 중단합니다 — 남은 파일은 건너뜁니다');
  };
  // 요약 칩 닫기 — 중복·실패를 사용자가 인지할 유일한 자리라 ✕ 를 누를 때까지 남는다.
  var dismissScan = function() {
    cancelRef.current = false;
    setOcrRows([]); setScanFiles([]); setUpDone(0); setScanStopped(0);
    reload();
  };
  var scanCount = function(kind) { return ocrRows.filter(function(r) { return r.kind === kind; }).length; };
  var ocrPending = (summary && summary.recent && parseInt(summary.recent.pending, 10)) || 0;
  var scanActive = scanRunning || ocrRows.length > 0 || scanStopped > 0 || ocrPending > 0;

  // ── 패널 높이 — 남은 화면을 채운다 (Jin 2026-08-28) ─────────────────────
  // 고정 618 이던 것: 큰 화면에선 아래가 통째로 비고(1080 에서 251px, 1400 에서 571px),
  // 낮은 화면에선 오히려 셸 밖으로 넘쳐 스크롤이 생겼다.
  // 조상들이 높이를 물려주지 않는 구조(각자 content 높이)라 flex 로는 안 되고 실측이 필요하다.
  useLayoutEffect(function() {                     // 첫 페인트 전에 재야 높이가 한 번 튀지 않는다
    if (isMobile) return undefined;                // 모바일은 스택 — 높이를 묶지 않는다
    var calc = function() {
      var el = paneRef.current;
      if (!el) return;
      var top = el.getBoundingClientRect().top;
      // 스크롤 주체는 셸의 .dash-content — 창 바닥보다 아래로 내려가 있을 수 있어 둘 중 위를 택한다
      var sc = el.closest ? el.closest('.dash-content') : null;
      var bottom = sc ? Math.min(sc.getBoundingClientRect().bottom, window.innerHeight) : window.innerHeight;
      setPaneH(Math.max(PANEL_MIN, Math.round(bottom - top - PANEL_GAP)));
    };
    calc();
    window.addEventListener('resize', calc);
    return function() { window.removeEventListener('resize', calc); };
    // scanActive 는 상단 스트립을 키워 패널 시작점을 밀어낸다 → 그때도 다시 잰다
  }, [tier, isMobile, scanActive, agencyId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── 인식 결과 실시간 수신 (소켓 `malso:ocr`) ────────────────────────────
  // 자리 행(ocrStatus='pending')을 **제자리에서** 갈아끼운다 — 목록 순서가 흔들리면 검수 흐름이 끊긴다.
  //   request    → replacedBy(요청 완료로 승격된 행)로 교체 + 뱃지
  //   duplicated → 행은 유지하고 뱃지 + dupOfId 보관 (사람이 원본을 비교해 지운다)
  //   failed     → '인식 실패' + [다시 인식] / [차량번호 입력]
  var applyOcr = function(d) {
    if (!d || !d.itemId) return;
    var kind = d.kind || (d.status === 'failed' ? 'failed' : 'unassigned');
    var id = parseInt(d.itemId, 10) || 0;
    setOcrRows(function(prev) {
      if (prev.some(function(r) { return r.key === ('it' + id); })) return prev;   // 재전송 멱등
      return prev.concat([{ key: 'it' + id, kind: kind }]);
    });
    setItems(function(prev) {
      var i = -1;
      prev.forEach(function(r, k) { if (r.id === id) i = k; });
      if (i < 0) return prev;
      var next = prev.slice();
      var rep = (kind === 'request' && d.replacedBy && d.replacedBy.id) ? d.replacedBy : null;
      var base = rep || Object.assign({}, next[i], d.item || {});
      next[i] = Object.assign({}, base, {
        ocrStatus: kind === 'failed' ? 'failed' : 'done',
        ocrKind: kind,
        requestId: d.requestId || base.requestId || 0,
        dupOfId: d.dupOfId || 0
      });
      return next;
    });
    loadBox();     // 레일 배지 · '인식 진행 중 N건' 칩의 모수 갱신
  };
  // 핸들러는 매 렌더 최신본으로 갈아끼우고, 구독은 1회만 (LivePopup 관례 + stale closure 방지)
  var ocrRef = useRef(applyOcr);
  ocrRef.current = applyOcr;
  useEffect(function() {
    var sock = getSocket();
    if (!sock) return undefined;
    var fn = function(d) { ocrRef.current(d); };
    sock.on('malso:ocr', fn);
    return function() {
      try { sock.off('malso:ocr', fn); } catch (e) {}
    };
  }, []);

  // 말소증 중복 등록 — 같은 차량이 이미 다른 요청/스캔으로 처리된 건에 재업로드 시 서버가 통지.
  // #cap-lock 계약: {itemId, carNo, requestId, completedByName, completedDate}. 목록은 그대로 재조회.
  var applyDuplicate = function(d) {
    if (!d) return;
    toast((d.carNo || '') + ' 는 이미 처리된 건입니다 (처리자 ' + (d.completedByName || '') + '). 중복으로 표시했습니다.', 'err');
    reload();
  };
  var dupRef = useRef(applyDuplicate);
  dupRef.current = applyDuplicate;
  useEffect(function() {
    var sock = getSocket();
    if (!sock) return undefined;
    var fn = function(d) { dupRef.current(d); };
    sock.on('malso:duplicate', fn);
    return function() {
      try { sock.off('malso:duplicate', fn); } catch (e) {}
    };
  }, []);

  // 폴링 폴백 — '신규 등록' 폴더에 자리 행이 보이는 동안만 10초 폴링, 0 이 되면 멈춘다.
  //   A3-5 (2026-08-27): 조건에서 소켓 연결 여부(socketOk)를 뺐다. admin/doc_agency 는 소켓이
  //   **연결은 됐지만** 'agency:{id}' 룸 밖일 수 있어(서버 join 범위) malso:ocr 을 못 받는데,
  //   종전 조건은 그 케이스에서 폴백까지 꺼서 "인식 대기중" 이 영구 고착했다.
  //   폴링의 종료 조건은 그대로 '자리 행 0' — 소켓이 정상 수신하면 pending 이 곧 0 이 되어 멈춘다.
  //   W-10 (2026-08-27): 상한 60회(=10분). OCR 큐가 멈춘 채 자리 행이 남으면 종료 조건이
  //   영영 성립하지 않아 탭을 열어둔 내내 10초마다 목록 2개를 계속 때렸다. 카운터는 effect
  //   지역 변수라 dep(폴더 전환·자리 행 수 변화 = 목록 재조회/화면 재진입) 이 바뀌면 리셋된다.
  var POLL_MAX_TICKS = 60;
  var visiblePending = items.filter(function(r) { return r.ocrStatus === 'pending'; }).length;
  useEffect(function() {
    if (!isRecent || visiblePending <= 0) return undefined;
    var ticks = 0;
    var t = setInterval(function() {
      ticks += 1;
      if (ticks > POLL_MAX_TICKS) { clearInterval(t); return; }
      loadBox(); loadItems(RECENT.key);
    }, 10000);
    return function() { clearInterval(t); };
  }, [isRecent, visiblePending]); // eslint-disable-line react-hooks/exhaustive-deps

  // 인식 실패 행 재인식 — 서버가 큐에 다시 넣고 결과는 같은 소켓으로 온다.
  var retryOcr = function(it) {
    authFetch(API + '/settlement-items/' + it.id + '/ocr-retry', { method: 'POST', body: JSON.stringify({}) })
      .then(function(r) { return r.json(); }).then(function(d) {
        if (!d || !d.success) { toast(describeError(d, '다시 인식할 수 없습니다'), 'err'); return; }
        toast('다시 인식합니다 — 결과가 나오면 자동으로 채워집니다');
        setItems(function(prev) {
          return prev.map(function(r) { return r.id === it.id ? Object.assign({}, r, { ocrStatus: 'pending', ocrKind: '' }) : r; });
        });
      }).catch(function() { toast('다시 인식할 수 없습니다', 'err'); });
  };

  // ── 수동 요청 연결 (미분류 행 ↔ 진행 중 요청) ──────────────────────────
  // 차량번호를 되짚어 입력하는 [차량번호 입력] 과 목적지는 같지만, 입력이 **요청 선택**이라
  // OCR 오인식에 영향받지 않는다. 서버 POST /settlement-items/:id/link-request 가
  // 요청 완료 파이프라인을 태우고 미분류 자리 행을 병합한다.
  var LINKABLE = ['접수', '진행중', '수거완료'];   // 서버 malsoBox.SCAN_MATCHABLE_STATUSES 와 동일
  var openLink = function(it) {
    setLinkItem(it); setLinkQ(''); setLinkReqs([]); setLinkLoading(true);
    authFetch(API + '/cancel-requests' + ((!isCancelAgency && agencyId) ? ('?agencyId=' + agencyId) : ''))
      .then(function(r) { return r.json(); })
      .then(function(d) {
        // 작업장 모드(paged=1)는 { rows }, 기본은 배열 — 둘 다 받는다.
        var list = Array.isArray(d) ? d : ((d && Array.isArray(d.rows)) ? d.rows : []);
        setLinkReqs(list.filter(function(x) { return LINKABLE.indexOf(x.status) >= 0; }));
        setLinkLoading(false);
      })
      .catch(function() { setLinkReqs([]); setLinkLoading(false); });
  };
  var doLink = function(rq) {
    if (!linkItem) return;
    if (!window.confirm('이 말소증을 ' + (rq.carNo || '선택한') + ' 요청에 연결하고 완료 처리합니다')) return;
    var itemId = linkItem.id;
    authFetch(API + '/settlement-items/' + itemId + '/link-request', {
      method: 'POST', body: JSON.stringify(scopeBody({ requestId: rq.id }))
    }).then(function(r) { return r.json(); }).then(function(d) {
      if (!d || !d.success) { toast(describeError(d, '연결하지 못했습니다'), 'err'); return; }
      setLinkItem(null);
      toast('요청 건으로 완료됐습니다');
      // 소켓 request 케이스와 **같은 코드**로 행을 제자리 치환 + 보관함 요약 재조회
      applyOcr({ itemId: itemId, status: 'done', kind: 'request', item: d.item, replacedBy: d.replacedBy, requestId: d.requestId });
    }).catch(function() { toast('연결하지 못했습니다', 'err'); });
  };

  // ── 스타일 ──────────────────────────────────────────────────────────────
  var inputSt = { padding: '7px 10px', border: '1px solid #d0d5dd', borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box', fontFamily: 'Pretendard,sans-serif', minHeight: isMobile ? 40 : undefined };
  var muted = { fontSize: isMobile ? 12 : 11.5, color: '#94a3b8' };
  var tapBtn = isMobile ? { minHeight: 40 } : undefined;
  // 다중 선택(체크박스)은 미분류 폴더 한정 — 검색 결과는 여러 폴더가 섞여 있어
  // 이미 귀속된 행까지 골라 assign-client 에 던지게 된다 (서버가 skip 하지만 UI 가 거짓말을 한다).
  // 선택(체크·Shift·일괄 삭제)은 미분류 + 신규 등록에서, 일괄 '지정'은 미분류에서만
  // (신규 등록엔 요청 완료·이미 귀속된 행이 섞여 있어 일괄 지정이 오귀속을 부른다 — 지정은 단건 패널로).
  var canSel = (isUnassigned || isRecent) && !isStaff && hits === null;
  var canBulk = isUnassigned && !isStaff && hits === null;
  // 체크 토글 — Shift 는 마지막으로 찍은 행부터 범위 일괄 (파일 탐색기 관례).
  // **이번 클릭이 만들 상태**를 범위 전체에 적용한다 (체크 → 범위 전부 체크 / 해제 → 범위 전부 해제).
  var toggleRow = function(it, shift) {
    var idx = rowIds.indexOf(it.id);
    var next = selected.indexOf(it.id) < 0;
    var last = anchorRef.current;
    if (shift && last >= 0 && idx >= 0 && last < rowIds.length && last !== idx) {
      var span = rowIds.slice(Math.min(last, idx), Math.max(last, idx) + 1);
      setSelected(function(prev) {
        var rest = prev.filter(function(x) { return span.indexOf(x) < 0; });
        return next ? rest.concat(span) : rest;
      });
    } else {
      setSelected(function(prev) {
        return next ? prev.concat([it.id]) : prev.filter(function(x) { return x !== it.id; });
      });
    }
    anchorRef.current = idx;
  };
  // 열 구성 (Jin 9/2) — 우측 '원본' 열 제거. 데스크탑은 우측 검수 패널, 모바일은 상단 미리보기에
  // 이미 원본이 떠 있고 거기에 [⤢ 원본] 이 있어 목록의 [보기] 는 기능 중복이었다.
  // 46px + gap 은 차량/소유자 열이 흡수한다(뱃지·차명이 잘리던 열).
  // ⚠ openDoc/hasDoc/뷰어 state 는 지우지 않는다 — 검수 패널(CancelAgencyMalsoReview)의
  //   [⤢ 원본] 이 onOpenDoc 으로 같은 것을 쓴다(공용, orphan 아님).
  var GRID = canSel ? '26px minmax(0,1fr) 78px 62px' : 'minmax(0,1fr) 78px 62px';

  // ── 렌더: 대행사 미선택 ─────────────────────────────────────────────────
  if (!canQuery()) {
    return (<div style={{ fontFamily: 'Pretendard,sans-serif' }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: '#1e3a5f' }}>대행업체</span>
        <select value={agencyId} onChange={function(e) { setAgencyId(e.target.value); }} style={inputSt}>
          <option value="">선택하세요</option>
          {(agencies || []).map(function(a) { return <option key={a.id} value={a.id}>{a.name}</option>; })}
        </select>
      </div>
      <div className="card" style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>대행업체를 선택하면 말소증 보관함이 열립니다.</div>
    </div>);
  }

  var kpi = (summary && summary.kpi) || { monthTotal: 0, monthRequest: 0 };
  var un = (summary && summary.unassigned) || { count: 0, needsReview: 0 };
  var rc = (summary && summary.recent) || { count: 0, pending: 0 };     // 오늘 등록분 총 건수 ('신규 등록' 배지)

  // ── 상단 등록 진행 스트립 (+ KPI) ───────────────────────────────────────
  var uploadBtn = function(full) {
    return (<button className="btn btn-blue" disabled={scanRunning} onClick={openScan}
      style={Object.assign({}, tapBtn, { width: full ? '100%' : undefined, justifyContent: 'center',
        padding: isMobile ? '11px 16px' : '9px 14px', fontSize: isMobile ? 14 : 13, fontWeight: 800,
        boxShadow: '0 2px 8px rgba(37,99,235,.35)' })}>말소증 등록</button>);
  };

  var strip = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: isMobile ? '10px 12px' : '9px 14px',
      background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, fontSize: 12, marginBottom: 12, flexWrap: 'wrap' }}>
      {!isCancelAgency && (
        <select value={agencyId} onChange={function(e) { setAgencyId(e.target.value); openFolder('unassigned', '미분류'); }}
          aria-label="대행업체" style={Object.assign({}, inputSt, { padding: '5px 8px', fontSize: 12 })}>
          <option value="">대행업체 선택</option>
          {(agencies || []).map(function(a) { return <option key={a.id} value={a.id}>{a.name}</option>; })}
        </select>
      )}
      {/* 모바일은 하단 고정 CTA 가 같은 [말소증 등록] 을 이미 크게 들고 있다 — 스트립에서 한 번 더 쌓지 않는다 */}
      {!scanRunning && !isMobile && (<React.Fragment>
        {uploadBtn(false)}
        {!scanActive && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px', flex: '0 1 auto',
            border: dragOver ? '1.5px solid #2563EB' : '1.5px dashed #bfdbfe', borderRadius: 9,
            background: dragOver ? '#dbeafe' : '#f8fbff', fontSize: 11.5, color: '#64748b' }}>
            <b style={{ color: '#1d4ed8', fontWeight: 800 }}>드래그 앤 드랍</b> 사진·PDF 를 화면에 끌어다 놓으세요
          </span>
        )}
      </React.Fragment>)}
      {scanActive && (<React.Fragment>
        {/* ① 업로드 구간 — 진행바 + 이탈 경고 + [업로드 취소]. 클라가 붙잡고 있는 유일한 구간. */}
        {scanRunning && (<React.Fragment>
          <span className="mbox-spin" aria-label="업로드 진행 중" style={{ width: 15, height: 15, flexShrink: 0, borderRadius: '50%',
            border: '2.5px solid #bfdbfe', borderTopColor: '#2563EB', animation: 'mboxspin .8s linear infinite' }} />
          <span style={{ fontWeight: 800, fontSize: 12, color: '#1B3A5C' }}>업로드 진행</span>
          <span style={{ position: 'relative', flex: isMobile ? '1 1 90px' : '0 1 auto', width: isMobile ? 'auto' : 132,
            height: 6, background: '#e2e8f0', borderRadius: 999, overflow: 'hidden' }}>
            <span style={{ position: 'absolute', top: 0, bottom: 0, left: 0, borderRadius: 999, background: '#2563EB',
              width: (scanFiles.length ? Math.round((upDone / scanFiles.length) * 100) : 0) + '%', transition: 'width .18s' }} />
          </span>
          <span aria-label="업로드 진행 수" style={CHIP}>업로드 <b>{upDone}/{scanFiles.length}</b></span>
          <button className="btn btn-outline btn-sm" aria-label="업로드 취소" onClick={cancelScan}
            style={Object.assign({ padding: '4px 10px', fontSize: 11.5, color: '#b91c1c', borderColor: '#fecaca' }, tapBtn)}>업로드 취소</button>
          <span style={{ fontSize: 11.5, fontWeight: 700, color: '#92400e', background: '#fffbeb',
            border: '1px solid #fcd34d', borderRadius: 999, padding: '3px 10px' }}>
            업로드가 끝날 때까지 이 화면을 벗어나지 마세요</span>
        </React.Fragment>)}
        {/* ② 인식 구간 — 서버 큐가 진행하므로 이탈해도 된다. 경고 없이 진행 칩만. */}
        {!scanRunning && ocrPending > 0 && (
          <span aria-label="인식 진행 칩" style={Object.assign({}, CHIP, CHIP_BLUE)}>
            <span className="mbox-spin" style={{ width: 11, height: 11, flexShrink: 0, borderRadius: '50%',
              border: '2px solid #bfdbfe', borderTopColor: '#2563EB', animation: 'mboxspin .8s linear infinite' }} />
            인식 진행 중 <b>{ocrPending}건</b>
          </span>
        )}
        {/* ③ 요약 칩 — 소켓 수신분이 쌓이고 ✕ 전까지 남는다 (중복·실패를 인지할 유일한 자리) */}
        {!scanRunning && ocrRows.length > 0 && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <span aria-label="요청 완료 건수" style={Object.assign({}, CHIP, CHIP_OK)}>요청 완료 <b>{scanCount('request')}</b></span>
            <span aria-label="미분류 건수" style={Object.assign({}, CHIP, CHIP_WARN)}>미분류 <b>{scanCount('unassigned')}</b></span>
            <span aria-label="중복 건수" style={Object.assign({}, CHIP, CHIP_DAN)}>중복 <b>{scanCount('duplicated')}</b></span>
            <span aria-label="실패 건수" style={Object.assign({}, CHIP, CHIP_DAN)}>실패 <b>{scanCount('failed')}</b></span>
          </span>
        )}
        {!scanRunning && scanStopped > 0 && <span style={{ fontSize: 11.5, color: '#b45309', fontWeight: 700 }}>중단됨 · {scanStopped}건 건너뜀</span>}
        {!scanRunning && (ocrRows.length > 0 || scanStopped > 0) && (
          <button aria-label="등록 결과 닫기" onClick={dismissScan}
            style={Object.assign({ border: 'none', background: 'none', color: '#94a3b8', fontSize: 15, lineHeight: 1, cursor: 'pointer', padding: '2px 4px' },
              isMobile ? { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 40, minHeight: 40, padding: 0, fontSize: 17 } : null)}>✕</button>
        )}
      </React.Fragment>)}
      <div style={{ marginLeft: isMobile ? 0 : 'auto', display: isMobile ? 'grid' : 'flex',
        gridTemplateColumns: isMobile ? '1fr 1fr 1fr' : undefined, width: isMobile ? '100%' : undefined,
        alignItems: 'center', gap: isMobile ? 8 : 16 }}>
        {kpiCell('이달 등록', kpi.monthTotal, false, isMobile)}
        {kpiCell('요청 완료', kpi.monthRequest, false, isMobile)}
        {kpiCell('미분류' + (un.needsReview ? (' (확인 필요 ' + un.needsReview + ')') : ''), un.count, true, isMobile)}
      </div>
    </div>
  );

  // ── 폴더 조각 ───────────────────────────────────────────────────────────
  var atCurrentMonth = month >= kstMonth();      // 미래 달 차단 (Jin 8/25) — 이후 달로 넘어가지 않는다
  var monthSwitch = (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <button className="btn btn-outline btn-sm" style={Object.assign({ padding: '2px 7px', fontSize: 11 }, tapBtn)} aria-label="이전 달"
        onClick={function() { setMonth(shiftMonth(month, -1)); }}>◀</button>
      <b style={{ fontSize: 12.5, color: '#1B3A5C', fontWeight: 800 }}>{month}</b>
      <button className="btn btn-outline btn-sm" disabled={atCurrentMonth}
        style={Object.assign({ padding: '2px 7px', fontSize: 11, opacity: atCurrentMonth ? 0.4 : 1 }, tapBtn)} aria-label="다음 달"
        onClick={function() { if (!atCurrentMonth) setMonth(shiftMonth(month, 1)); }}>▶</button>
    </span>
  );


  var rail = (
    <div className="card" style={Object.assign({}, CARD, { height: paneH, display: 'flex', flexDirection: 'column' })}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 10px', borderBottom: '1px solid #f1f5f9', background: '#f8fafc' }}>
        {monthSwitch}
      </div>
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '0 8px 8px' }}>
        {/* 신규 등록 — 오늘 올린 것만 모아 보는 자리. 미분류 위 고정 (등록 직후 첫 동선) */}
        <div role="button" tabIndex={0} aria-label="신규 등록 폴더"
          onClick={function() { openFolder(RECENT.key, RECENT.name); }}
          onKeyDown={function(e) { if (e.key === 'Enter') openFolder(RECENT.key, RECENT.name); }}
          style={Object.assign({}, FOLDER, { background: '#eff6ff', borderColor: '#bfdbfe', color: '#1d4ed8', fontWeight: 800 },
            isRecent ? { boxShadow: 'inset 3px 0 0 #2563EB' } : null)}>
          <span style={{ flex: 1, minWidth: 0 }}>신규 등록
            <span style={{ display: 'block', fontSize: 10.5, color: ocrPending > 0 ? '#1d4ed8' : '#60a5fa', fontWeight: ocrPending > 0 ? 800 : 600 }}>
              {ocrPending > 0 ? ('오늘 등록분 (인식 중 ' + ocrPending + ')') : '오늘 등록분'}</span>
          </span>
          <span style={{ fontSize: 11, fontWeight: 800, background: '#2563EB', color: '#fff', borderRadius: 999, padding: '1px 7px' }}>{rc.count}</span>
        </div>
        <div role="button" tabIndex={0} aria-label="미분류 폴더"
          onClick={function() { openFolder('unassigned', '미분류'); }}
          onKeyDown={function(e) { if (e.key === 'Enter') openFolder('unassigned', '미분류'); }}
          style={Object.assign({}, FOLDER, { background: '#fffbeb', borderColor: '#fcd34d', color: '#92400e', fontWeight: 800 },
            isUnassigned ? { boxShadow: 'inset 3px 0 0 #f59e0b' } : null)}>
          <span style={{ flex: 1, minWidth: 0 }}>미분류
            {un.needsReview ? <span style={{ display: 'block', fontSize: 10.5, color: '#b45309', fontWeight: 600 }}>확인 필요 {un.needsReview}</span> : null}
          </span>
          <span style={{ fontSize: 11, fontWeight: 800, background: '#f59e0b', color: '#fff', borderRadius: 999, padding: '1px 7px' }}>{un.count}</span>
        </div>
        {/* 섹션 타이틀 대신 얇은 구분선 — 위(신규 등록·미분류)와 아래(거래처 폴더)를 시각으로만 나눈다 (Jin) */}
        <div style={{ borderTop: '1px solid #f1f5f9', margin: '12px 2px 8px' }} />
        <div style={{ display: 'flex', gap: 4, margin: '0 0 6px' }}>
          <input value={railQ} onChange={function(e) { setRailQ(e.target.value); }}
            placeholder="거래처 검색" aria-label="거래처 폴더 검색"
            style={{ flex: 1, minWidth: 0, boxSizing: 'border-box', padding: '6px 8px', border: '1px solid #e2e8f0',
              borderRadius: 7, fontSize: 11.5, outline: 'none', fontFamily: 'inherit' }} />
          <select value={railSort} onChange={function(e) { setRailSort(e.target.value); }} aria-label="거래처 폴더 정렬"
            style={{ flexShrink: 0, padding: '5px 4px', border: '1px solid #e2e8f0', borderRadius: 7, fontSize: 10.5,
              fontWeight: 700, color: railSort === 'recent' ? '#94a3b8' : '#1d4ed8', fontFamily: 'inherit', background: '#fff' }}>
            {RAIL_SORTS.map(function(o) { return <option key={o[0]} value={o[0]}>{o[1]}</option>; })}
          </select>
        </div>
        {summary && sortFolders(summary.folders).filter(function(f) {
          var q = railQ.replace(/\s/g, '').toLowerCase();
          return !q || String(f.name || '').replace(/\s/g, '').toLowerCase().indexOf(q) >= 0;
        }).map(function(f) {
          var on = folder && folder.key === f.key;
          return (<div key={f.key} role="button" tabIndex={0} aria-label={'폴더 ' + f.name}
            onClick={function() { openFolder(f.key, f.name); }}
            onKeyDown={function(e) { if (e.key === 'Enter') openFolder(f.key, f.name); }}
            style={Object.assign({}, FOLDER, on ? { background: '#eff6ff', borderColor: '#bfdbfe', color: '#1d4ed8', fontWeight: 800 } : null)}>
            <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 600 }}>{f.name}
              {f.lastDate ? <span style={{ display: 'block', fontSize: 10.5, color: '#94a3b8', fontWeight: 500 }}>{f.lastDate}</span> : null}
            </span>
            <span style={{ fontSize: 11, fontWeight: 800, color: '#64748b', background: '#f1f5f9', borderRadius: 999, padding: '1px 7px' }}>{f.monthCount}</span>
          </div>);
        })}
        {summary && summary.folders.length === 0 && (
          <div style={{ padding: '14px 9px', fontSize: 11.5, color: '#94a3b8', lineHeight: 1.5 }}>{month} 에 귀속된 말소증이 없습니다.</div>
        )}
      </div>
      {/* 등록 버튼·드랍 존은 상단 스트립으로 이동 (Jin 8/25 — 평시 빈 스트립 해소) */}
    </div>
  );

  // 태블릿/모바일 — 레일이 접혀 상단 가로 칩 줄이 된다
  // 모바일 칩은 터치 타깃 (기본 FCHIP 은 29px — 손가락에 안 맞는다)
  var fchip = function (extra, on) { return Object.assign({}, FCHIP, isMobile ? FCHIP_TAP : null, extra, on); };
  var chipBar = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
      {monthSwitch}
      {/* 거래처 폴더 검색·정렬 — 레일(데스크탑)에만 있던 것을 모바일 칩 줄에도 (Jin 8/26 실기).
          칩 줄은 가로 스크롤이라 폴더가 늘면 손으로 훑어야 했다. 태블릿은 레일도 이 줄도 아닌
          기존 그대로 (railQ/railSort 를 만질 수단이 없어 값이 초깃값으로 고정 = 렌더 불변). */}
      {isMobile && (
        <div style={{ display: 'flex', gap: 6, flex: '1 1 150px', minWidth: 0 }}>
          <input value={railQ} onChange={function(e) { setRailQ(e.target.value); }}
            placeholder="거래처 검색" aria-label="거래처 폴더 검색"
            style={Object.assign({}, inputSt, { flex: 1, minWidth: 0, padding: '6px 9px', fontSize: 12 })} />
          <select value={railSort} onChange={function(e) { setRailSort(e.target.value); }} aria-label="거래처 폴더 정렬"
            style={Object.assign({}, inputSt, { flexShrink: 0, padding: '5px 6px', fontSize: 11.5, fontWeight: 700,
              color: railSort === 'recent' ? '#94a3b8' : '#1d4ed8', background: '#fff' })}>
            {RAIL_SORTS.map(function(o) { return <option key={o[0]} value={o[0]}>{o[1]}</option>; })}
          </select>
        </div>
      )}
      <div style={{ flex: isMobile ? '1 1 100%' : 1, minWidth: 0, overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', width: 'max-content', padding: '2px 0' }}>
          <button aria-label="신규 등록 폴더" onClick={function() { openFolder(RECENT.key, RECENT.name); }}
            style={fchip({ background: '#eff6ff', borderColor: '#bfdbfe', color: '#1d4ed8' },
              isRecent ? { boxShadow: 'inset 0 0 0 1.5px #2563EB' } : null)}>신규 등록 <b>{rc.count}</b>
            {ocrPending > 0 ? <span style={{ fontWeight: 800 }}>(인식 중 {ocrPending})</span> : null}</button>
          <button aria-label="미분류 폴더" onClick={function() { openFolder('unassigned', '미분류'); }}
            style={fchip({ background: '#fffbeb', borderColor: '#fcd34d', color: '#92400e' },
              isUnassigned ? { boxShadow: 'inset 0 0 0 1.5px #f59e0b' } : null)}>미분류 <b>{un.count}</b></button>
          {summary && sortFolders(summary.folders).filter(function(f) {
            var k = railQ.replace(/\s/g, '').toLowerCase();
            return !k || String(f.name || '').replace(/\s/g, '').toLowerCase().indexOf(k) >= 0;
          }).map(function(f) {
            var on = folder && folder.key === f.key;
            return (<button key={f.key} aria-label={'폴더 ' + f.name} onClick={function() { openFolder(f.key, f.name); }}
              style={fchip(null, on ? { background: '#eff6ff', borderColor: '#2563EB', color: '#1d4ed8' } : null)}>{f.name} <b>{f.monthCount}</b></button>);
          })}
        </div>
      </div>
    </div>
  );

  // ── 목록 조각 ───────────────────────────────────────────────────────────
  // 자리 행(ocrStatus='pending') — 차량번호 자리에 스피너 + '인식 대기중…'.
  // 소켓이 결과를 주면 이 행이 제자리에서 실제 값으로 바뀐다.
  var carNoCell = function(it) {
    if (it.ocrStatus === 'pending') {
      return (<span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 700, color: '#1d4ed8' }}>
        <span className="mbox-spin" aria-label="인식 대기중" style={{ width: 12, height: 12, flexShrink: 0, borderRadius: '50%',
          border: '2px solid #bfdbfe', borderTopColor: '#2563EB', animation: 'mboxspin .8s linear infinite' }} />
        인식 대기중…
      </span>);
    }
    return it.carNo
      ? <span style={{ fontSize: 13, fontWeight: 800, letterSpacing: -0.2, color: '#1B3A5C' }}>{it.carNo}</span>
      : <span style={{ fontSize: 13, fontWeight: 700, color: '#dc2626' }}>─ 인식 실패</span>;
  };
  // 정보 위계 (Jin 9/2 — "차량/소유자 열에 너무 많은 정보가 몰려있다")
  //   1줄 = 식별자 + **지금 조치가 필요한 것** : 차량번호 / [확인 필요] [중복] / [다시 인식] [차량번호 입력]
  //   2줄 = 부가 메타                          : 차명·구청·소유자 + [new] [요청] [폴더명]
  // 줄 수는 그대로 2줄이다 — 밀도가 자산이라 늘리지 않고 무엇이 어느 줄에 가는지만 바꾼다.
  // 뱃지 총량도 줄였다:
  //   · '요청 완료'(ocrKind, 소켓으로 방금 승격된 순간 표시 — 새로고침하면 사라진다)와
  //     '요청'(source, 서버 컬럼의 영구 속성)은 **같은 축의 순간/영구 표현**이라 눈에 거의 같아 보였다.
  //     → [요청] 하나로 합치고, 방금 승격된 건은 title 툴팁으로만 구분(정보 유실 없음 —
  //       재조회하면 그 행은 어차피 source='request' 가 되어 같은 뱃지가 된다).
  //   · 폴더명 뱃지는 폴더 필터가 걸린 동안 좌측 레일과 중복이라 **검색 결과일 때만** 표시.
  //     (헤더에서 같은 이유로 이미 뺐다 — Jin 8/26. 검수 패널 folderName prop 도 같은 분기다.)
  var reqLabel = function(it) {
    if (it.ocrKind === 'request') return '방금 대행요청과 대조되어 요청 완료로 승격된 건';
    return it.source === 'request' ? '대행요청 경로로 등록된 건' : '';
  };
  // 행 안 인라인 액션 — 모바일은 손가락 타깃(≈36px)으로 키운다 (데스크탑은 기존 10px 미니 버튼 유지)
  var rowMiniBtn = function() {
    return isMobile ? { fontSize: 11.5, padding: '0 11px', borderRadius: 7, minHeight: 36, display: 'inline-flex', alignItems: 'center' } : null;
  };
  // 1줄 — 차량번호 옆에 붙는 '지금 조치' 뱃지 + 액션
  var statusLine = function(it) {
    var pend = it.ocrStatus === 'pending';
    var failed = it.ocrStatus === 'failed';
    var miniBtn = rowMiniBtn();
    var carNoBtn = (
      <button onClick={function(e) { e.stopPropagation(); setCarNoEdit({ id: it.id, value: it.carNo || '' }); }}
        style={Object.assign({ fontSize: 10, fontWeight: 700, color: '#dc2626', border: '1px solid #fecaca', borderRadius: 5, padding: '1px 6px', background: '#fff', cursor: 'pointer' }, miniBtn)}>차량번호 입력</button>
    );
    return (<div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', minWidth: 0 }}>
      {itemPresenceBadge(it.id)}
      {carNoCell(it)}
      {!pend && it.needsReview ? <span style={Object.assign({}, BDG, BDG_WARN)}>확인 필요</span> : null}
      {it.ocrKind === 'duplicated' ? <span title={it.dupOfId ? ('중복 원본 #' + it.dupOfId) : ''} style={Object.assign({}, BDG, BDG_DAN)}>중복</span> : null}
      {/* M4: 서버 목록(si.*)이 내려주는 영구 컬럼 — OCR 세션 뱃지(ocrKind, 새로고침 시 사라짐)와 달리 재조회해도 유지 */}
      {it.ocrKind !== 'duplicated' && it.duplicate_of_request_id > 0 ? <span title={it.duplicate_note || ''} style={Object.assign({}, BDG, BDG_GRAY)}>중복</span> : null}
      {failed && (
        <button aria-label={'다시 인식 ' + it.id} onClick={function(e) { e.stopPropagation(); retryOcr(it); }}
          style={Object.assign({ fontSize: 10, fontWeight: 700, color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: 5, padding: '1px 6px', background: '#fff', cursor: 'pointer' }, miniBtn)}>다시 인식</button>
      )}
      {failed ? carNoBtn : (isUnassigned && !pend && !it.carNo ? carNoBtn : null)}
    </div>);
  };
  // 2줄 — 참고용 메타. 텍스트가 먼저, 참고 뱃지는 뒤.
  var metaLine = function(it) {
    var pend = it.ocrStatus === 'pending';
    var parts = [it.carType, it.district, it.owner].filter(function(x) { return !!x; });
    var isReq = it.ocrKind === 'request' || it.source === 'request';
    return (<div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
      <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {pend ? (it.sourceFile || '업로드한 파일') : (parts.length ? parts.join(' · ') : '차명·구청 공란')}
      </span>
      {it.isNew ? <span style={Object.assign({}, BDG, BDG_NEW)}>new</span> : null}
      {isReq ? <span title={reqLabel(it)} style={Object.assign({}, BDG, BDG_BLUE)}>요청</span> : null}
      {hits !== null && it.folderName ? <span style={Object.assign({}, BDG, BDG_GRAY)}>{it.folderName}</span> : null}
    </div>);
  };
  // 데스크탑·모바일이 같은 위계를 쓴다 (한 곳만 고치면 둘 다 따라온다)
  var rowBody = function(it) {
    return (<div style={{ minWidth: 0 }}>{statusLine(it)}{metaLine(it)}</div>);
  };

  var desktopRow = function(it) {
    var on = selected.indexOf(it.id) >= 0;
    var cur = it.id === selId;
    return (<div key={it.id} role="button" tabIndex={0} aria-label={'항목 ' + (it.carNo || it.id)}
      onMouseDown={function(e) { if (e.shiftKey) e.preventDefault(); }}   /* Shift 클릭이 텍스트를 긁지 않게 */
      onClick={function(e) { if (canSel && e.shiftKey) { toggleRow(it, true); return; } setSelId(it.id); }}
      onKeyDown={function(e) { if (e.key === 'Enter') setSelId(it.id); }}
      style={Object.assign({ display: 'grid', gridTemplateColumns: GRID, gap: 8, alignItems: 'center',
        padding: '9px 12px', borderBottom: '1px solid #f1f5f9', cursor: 'pointer',
        borderLeftWidth: 3, borderLeftStyle: 'solid', borderLeftColor: 'transparent' },
        cur ? { background: '#eff6ff', borderLeftColor: '#2563EB', boxShadow: 'inset 0 0 0 1px #bfdbfe' } : null)}>
      {canSel && (
        <input type="checkbox" checked={on} aria-label={'선택 ' + (it.carNo || it.id)}
          onMouseDown={function(e) { e.stopPropagation(); }}
          onChange={function() {}}
          onClick={function(e) { e.stopPropagation(); toggleRow(it, e.shiftKey); }} />
      )}
      {rowBody(it)}
      <div style={{ fontSize: 11.5, color: it.date ? '#475569' : '#cbd5e1', fontWeight: 600 }}>{it.date ? it.date.slice(5) : '—'}</div>
      <div style={{ fontSize: 11.5, fontWeight: it.subtotal ? 800 : 600, textAlign: 'right', color: it.subtotal ? '#334155' : '#cbd5e1' }}>{fmt(it.subtotal)}</div>
    </div>);
  };

  // 모바일 행 — 데스크탑과 같은 정보 구성(차량/메타 + 말소일 + 금액) + **체크박스**.
  // 체크가 없으면 '일괄 삭제 / 일괄 거래처 지정' 이 모바일에서 아예 도달 불가였다 (Jin 8/26 실기).
  // Shift 범위 선택은 데스크탑 고유 — 여기선 단건 토글만 (toggleRow 두 번째 인자 false).
  var mobileRow = function(it) {
    var on = selected.indexOf(it.id) >= 0;
    return (<div key={it.id} role="button" tabIndex={0} aria-label={'항목 ' + (it.carNo || it.id)}
      onClick={function() { setSelId(it.id); }}
      onKeyDown={function(e) { if (e.key === 'Enter') setSelId(it.id); }}
      style={Object.assign({ display: 'flex', alignItems: 'center', gap: 10, padding: '13px 12px', borderBottom: '1px solid #f1f5f9', cursor: 'pointer' },
        on ? { background: '#eff6ff' } : null)}>
      {canSel && (
        <span onClick={function(e) { e.stopPropagation(); toggleRow(it, false); }}
          style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 40, height: 40, marginLeft: -10, marginRight: -6 }}>
          <input type="checkbox" checked={on} aria-label={'선택 ' + (it.carNo || it.id)}
            onChange={function() {}} onClick={function(e) { e.stopPropagation(); toggleRow(it, false); }}
            style={{ width: 18, height: 18 }} />
        </span>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>{rowBody(it)}</div>
      <div style={{ textAlign: 'right' }}>
        <div style={{ fontSize: 11.5, color: it.date ? '#475569' : '#cbd5e1', fontWeight: 600 }}>{it.date ? it.date.slice(5) : '—'}</div>
        {it.subtotal ? <div style={{ fontSize: 11.5, fontWeight: 800, color: '#334155' }}>{fmt(it.subtotal)}</div> : null}
      </div>
      <span style={{ color: '#cbd5e1', fontSize: 20, lineHeight: 1 }}>›</span>
    </div>);
  };

  var sumAssigned = rows.filter(function(r) { return (r.subtotal || 0) > 0; });
  var sumAmount = sumAssigned.reduce(function(a, r) { return a + (parseInt(r.subtotal, 10) || 0); }, 0);

  var list = (
    <div className="card" style={Object.assign({}, CARD, isMobile ? null : { height: paneH, display: 'flex', flexDirection: 'column' })}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc', flexWrap: 'wrap' }}>
        {/* 폴더명 뱃지·기간 텍스트 제거 (Jin 8/26) — 좌측 레일 선택이 이미 말해준다. 검색 결과만 표시 */}
        {hits !== null && (
          <span style={Object.assign({}, BDG, BDG_BLUE, { fontSize: 11 })}>검색 결과</span>
        )}
        <span style={{ fontSize: 13.5, fontWeight: 800, color: '#1B3A5C' }}>{searching ? '검색 중…' : (rows.length + '건')}</span>
        <div style={{ flex: 1 }} />
        <input value={q} onChange={function(e) { setQ(e.target.value); }}
          onKeyDown={function(e) { if (e.key === 'Enter') runSearch(); }}
          placeholder="차량번호 검색" aria-label="차량번호 검색"
          style={Object.assign({ width: 150 }, inputSt, { padding: '5px 9px', fontSize: 12 },
            isMobile ? { width: 'auto', flex: '1 1 110px', minWidth: 0 } : null)} />
        <button className="btn btn-outline btn-sm" style={tapBtn} onClick={runSearch} disabled={searching}>🔍</button>
        {hits !== null && <button className="btn btn-outline btn-sm" style={tapBtn} onClick={clearSearch}>검색 해제</button>}
      </div>
      {canSel && selected.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 12px', background: '#eff6ff', borderBottom: '1px solid #dbeafe', fontSize: 11.5, color: '#1d4ed8', fontWeight: 700, flexWrap: 'wrap' }}>
          <span>{selected.length}건 선택됨</span>
          {canBulk && (<button className="btn btn-blue btn-sm" style={Object.assign({ padding: '4px 9px', fontSize: 11.5 }, tapBtn)}
            onClick={function() { openAssign(selected.slice()); }}>일괄 거래처 지정 · {selected.length}건</button>)}
          <button className="btn btn-outline btn-sm" aria-label="선택 삭제" style={Object.assign({ padding: '4px 9px', fontSize: 11.5, color: '#dc2626', borderColor: '#fecaca' }, tapBtn)}
            onClick={deleteSelected}>삭제</button>
          <div style={{ flex: 1 }} />
          {/* 모바일엔 '전체 선택' 머리 행(그리드 헤더)이 없다 — 툴바에서 대신 준다 */}
          {isMobile && rows.length > selected.length && (
            <button className="btn btn-outline btn-sm" aria-label="전체 선택" style={Object.assign({ padding: '4px 9px', fontSize: 11.5 }, tapBtn)}
              onClick={function() { setSelected(rowIds.slice()); }}>전체 선택</button>
          )}
          <button className="btn btn-outline btn-sm" style={Object.assign({ padding: '4px 9px', fontSize: 11.5 }, tapBtn)}
            onClick={function() { setSelected([]); }}>선택 해제</button>
        </div>
      )}
      {!isMobile && (
        <div style={{ display: 'grid', gridTemplateColumns: GRID, gap: 8, alignItems: 'center', padding: '6px 12px',
          background: '#f8fafc', borderBottom: '1px solid #e2e8f0', fontSize: 10.5, fontWeight: 800, color: '#94a3b8', letterSpacing: 0.3 }}>
          {canSel && (
            <input type="checkbox" aria-label="전체 선택"
              checked={rows.length > 0 && selected.length === rows.length}
              onChange={function() { setSelected(selected.length === rows.length ? [] : rowIds.slice()); }} />
          )}
          <span>차량 / 소유자</span><span>말소일</span><span style={{ textAlign: 'right' }}>금액</span>
        </div>
      )}
      <div style={isMobile ? undefined : { flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {loading || searching
          ? <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>불러오는 중…</div>
          : rows.map(isMobile ? mobileRow : desktopRow)}
        {!loading && !searching && rows.length === 0 && (
          <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8', fontSize: 12.5 }}>
            {hits !== null ? '일치하는 차량번호가 없습니다.'
              : (isUnassigned ? '미분류 말소증이 없습니다.'
                : (isRecent ? '오늘 등록한 말소증이 없습니다.' : (month + ' 에 등록된 말소증이 없습니다.')))}
          </div>
        )}
      </div>
      <div style={{ padding: '8px 12px', borderTop: '1px solid #f1f5f9', background: '#f8fafc', fontSize: 11, color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={Object.assign({}, BDG, BDG_GRAY)}>{(isUnassigned ? '미분류 ' : '') + rows.length}</span>
        <span>정산 귀속 {sumAssigned.length}건 · 금액 합계 {fmt(sumAmount)}원</span>
      </div>
    </div>
  );

  var review = (
    <CancelAgencyMalsoReview
      item={current} index={selIndex < 0 ? 0 : selIndex} total={rows.length}
      folderName={hits !== null ? (current && current.folderName) : folder.name}
      clientName={current && current.clientId ? (function() {
        var f = (summary && summary.folders || []).filter(function(x) { return x.key === 'client:' + current.clientId; })[0];
        return f ? f.name : '';
      })() : ''}
      isStaff={isStaff} showToast={showToast}
      presenceBanner={current ? itemPresenceBanner(current.id) : null}
      overlay={isMobile} workTypes={workTypes} paneH={paneH}
      onPrev={function() { if (selIndex > 0) setSelId(rowIds[selIndex - 1]); }}
      onNext={function() { if (selIndex >= 0 && selIndex < rowIds.length - 1) setSelId(rowIds[selIndex + 1]); }}
      onClose={function() { setSelId(0); }}
      onOpenDoc={openDoc}
      onAssign={function(it) { openAssign([it.id]); }}
      onLinkRequest={openLink}
      pending={pending}
      onPendingSubmit={submitPending}
      onPendingCancel={function() { setPending(null); }}
      onDelete={deleteItem}
      onSaved={reload}
      onPromoted={function() { setSelId(0); reload(); }} />
  );

  // ── 렌더 ────────────────────────────────────────────────────────────────
  return (
    <div style={{ fontFamily: 'Pretendard,-apple-system,BlinkMacSystemFont,sans-serif', position: 'relative' }}
      onDragOver={function(e) { e.preventDefault(); if (!dragOver) setDragOver(true); }}
      onDragLeave={function(e) { if (e.currentTarget === e.target) setDragOver(false); }}
      onDrop={onBannerDrop}>

      {/* 스피너 키프레임 — 업로드 스트립과 자리 행이 함께 쓴다 (한 곳에서만 선언) */}
      <style>{'@keyframes mboxspin{to{transform:rotate(360deg)}} @media (prefers-reduced-motion: reduce){.mbox-spin{animation:none !important}}'}</style>
      {strip}

      {tier === 'desktop' && (
        <div ref={paneRef} style={{ display: 'grid', gridTemplateColumns: '190px minmax(280px,1fr) 62%', gap: 12, alignItems: 'start' }}>
          {rail}{list}{review}
        </div>
      )}
      {tier === 'tablet' && (<React.Fragment>
        {chipBar}
        <div ref={paneRef} style={{ display: 'grid', gridTemplateColumns: 'minmax(280px,1fr) 62%', gap: 12, alignItems: 'start' }}>
          {list}{review}
        </div>
      </React.Fragment>)}
      {tier === 'mobile' && (<React.Fragment>
        {chipBar}
        {list}
        {/* 하단 고정 CTA — iOS 홈 인디케이터/안드로이드 제스처 바에 안 깔리게 안전영역만큼 더 띄운다 */}
        <div style={{ position: 'sticky', bottom: 0, zIndex: 5, marginTop: 12, background: '#f8fafc', borderTop: '1px solid #e2e8f0',
          paddingTop: 10, paddingBottom: 'calc(6px + env(safe-area-inset-bottom))' }}>
          {uploadBtn(true)}
          <div style={{ textAlign: 'center', fontSize: 11, color: '#94a3b8', marginTop: 5 }}>사진 다중 선택 · 촬영 · 파일</div>
        </div>
        {current && review}
      </React.Fragment>)}

      {/* 화면 전역 드래그 오버 하이라이트 — 어디에 떨어뜨려도 등록 모달이 열린다 */}
      {dragOver && (
        <div style={{ position: 'absolute', inset: 0, zIndex: 20, border: '2px solid #2563EB', borderRadius: 12,
          background: 'rgba(219,234,254,.72)', display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
          <span style={{ fontSize: 17, fontWeight: 800, color: '#1d4ed8' }}>여기에 놓으면 등록됩니다</span>
        </div>
      )}

      {/* ── 거래처 고르기 팝업 ── 여기서는 거래처만 고른다 (금액 확인/수정은 우측 검수 패널) */}
      {assignOpen && sheet(isMobile, assignMode === 'change' ? '거래처 변경' : '거래처 지정', function() { setAssignOpen(false); setAssignClientId(''); }, (
        <div>
          <p style={{ margin: '0 0 10px', fontSize: 12.5, color: '#64748b', lineHeight: 1.5 }}>
            {assignIds.length > 1
              ? <React.Fragment>선택한 <b>{assignIds.length}건</b>을 보낼 거래처를 고르세요.</React.Fragment>
              /* '오른쪽' 은 3-pane 얘기다 — 모바일은 검수 화면이 풀스크린이라 방향 대신 순서로 말한다 */
              : (assignMode === 'change' ? '이 항목을 옮길 거래처를 고르세요. ' : '이 항목을 맡길 거래처를 고르세요. ')
                + (isMobile ? '고른 뒤 검수 화면에서 금액을 확인하고 등록합니다.' : '고른 뒤 오른쪽에서 금액을 확인하고 등록합니다.')}
          </p>
          <input value={assignQ} onChange={function(e) { setAssignQ(e.target.value); }}
            placeholder="거래처 검색" aria-label="거래처 검색"
            style={Object.assign({ width: '100%' }, inputSt)} />
          <div aria-label="거래처 목록" style={{ marginTop: 8, maxHeight: 260, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 9 }}>
            {clients.filter(function(c) {
              var k = assignQ.replace(/\s/g, '').toLowerCase();
              return !k || String(c.name || '').replace(/\s/g, '').toLowerCase().indexOf(k) >= 0;
            }).map(function(c) {
              var on = String(assignClientId) === String(c.id);
              return (<button key={c.id} aria-label={'거래처 ' + c.name} onClick={function() { pickAssignClient(c); }}
                style={Object.assign({ display: 'block', width: '100%', textAlign: 'left', padding: '9px 11px',
                  border: 'none', borderBottom: '1px solid #f1f5f9', background: on ? '#eff6ff' : '#fff',
                  fontSize: 13, fontWeight: on ? 800 : 600, color: on ? '#1d4ed8' : '#334155',
                  cursor: 'pointer', fontFamily: 'Pretendard,sans-serif' }, tapBtn)}>{c.name}</button>);
            })}
            {clients.length === 0 && <div style={{ padding: 16, textAlign: 'center', fontSize: 12, color: '#94a3b8' }}>등록된 거래처가 없습니다.</div>}
          </div>
          {assignIds.length > 1 && assignClientId && (
            <div style={{ marginTop: 12 }}>
              <div style={{ padding: '9px 11px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 9, fontSize: 13, color: '#1d4ed8', fontWeight: 700 }}>
                {assignIds.length}건을 <b>{(clients.filter(function(c) { return String(c.id) === String(assignClientId); })[0] || {}).name}</b> 로 지정합니다
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <button className="btn btn-blue" aria-label="일괄 지정 확정"
                  style={Object.assign({ flex: 1, justifyContent: 'center' }, tapBtn)} onClick={doAssign}>지정</button>
                <button className="btn btn-outline" style={Object.assign({ justifyContent: 'center', color: '#94a3b8' }, tapBtn)}
                  onClick={function() { setAssignOpen(false); setAssignClientId(''); }}>취소</button>
              </div>
            </div>
          )}
        </div>
      ))}

      {/* ── 수기 차량번호 입력 (목록 빠른 액션) ── */}
      {carNoEdit && sheet(isMobile, '차량번호 입력', function() { setCarNoEdit(null); }, (
        <div>
          <p style={{ margin: '0 0 12px', fontSize: 13, color: '#64748b', lineHeight: 1.5 }}>
            입력하면 진행 중인 말소 요청과 다시 대조합니다. 적중하면 그 요청이 자동 완료됩니다.
          </p>
          <input value={carNoEdit.value} aria-label="차량번호"
            onChange={function(e) { var v = e.target.value; setCarNoEdit(function(p) { return { id: p.id, value: v }; }); }}
            placeholder="예: 12가3456" style={Object.assign({ width: '100%' }, inputSt)} />
          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <button className="btn btn-blue" aria-label="차량번호 저장" style={Object.assign({ flex: 1, justifyContent: 'center' }, tapBtn)} onClick={saveCarNo}>저장</button>
            <button className="btn btn-outline" style={Object.assign({ justifyContent: 'center', color: '#94a3b8' }, tapBtn)} onClick={function() { setCarNoEdit(null); }}>취소</button>
          </div>
        </div>
      ))}

      {/* ── 수동 요청 연결 — 진행 중 요청 고르기 (OCR 오인식으로 자동 대조가 빗나간 건) ── */}
      {linkItem && sheet(isMobile, '요청에 연결', function() { setLinkItem(null); }, (
        <div>
          <p style={{ margin: '0 0 10px', fontSize: 12.5, color: '#64748b', lineHeight: 1.5 }}>
            이 말소증의 차량을 진행 중인 요청에서 고르세요. 고르면 그 요청이 완료 처리되고 말소증이 차량에 저장됩니다.
          </p>
          <input value={linkQ} onChange={function(e) { setLinkQ(e.target.value); }}
            placeholder="차량번호 · 수출업체 검색" aria-label="요청 검색"
            style={Object.assign({ width: '100%' }, inputSt)} />
          <div aria-label="요청 목록" style={{ marginTop: 8, maxHeight: 300, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 9 }}>
            {linkLoading && <div style={{ padding: 16, textAlign: 'center', fontSize: 12, color: '#94a3b8' }}>불러오는 중…</div>}
            {!linkLoading && linkReqs.filter(function(rq) {
              var k = linkQ.replace(/\s/g, '').toLowerCase();
              if (!k) return true;
              return (String(rq.carNo || '') + String(rq.requesterCompanyName || ''))
                .replace(/\s/g, '').toLowerCase().indexOf(k) >= 0;
            }).map(function(rq) {
              return (<button key={rq.id} aria-label={'요청 ' + (rq.carNo || rq.id)} onClick={function() { doLink(rq); }}
                style={Object.assign({ display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left',
                  padding: '9px 11px', border: 'none', borderBottom: '1px solid #f1f5f9', background: '#fff',
                  cursor: 'pointer', fontFamily: 'Pretendard,sans-serif' }, tapBtn)}>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <b style={{ display: 'block', fontSize: 13, fontWeight: 800, color: '#1B3A5C' }}>{rq.carNo || '(차량번호 없음)'}</b>
                  <span style={{ display: 'block', fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                    {[rq.carType, rq.requesterCompanyName].filter(function(x) { return !!x; }).join(' · ') || '차종·업체 정보 없음'}
                  </span>
                </span>
                <span style={Object.assign({}, BDG, BDG_BLUE)}>{rq.status}</span>
              </button>);
            })}
            {!linkLoading && linkReqs.length === 0 && (
              <div style={{ padding: 16, textAlign: 'center', fontSize: 12, color: '#94a3b8', lineHeight: 1.6 }}>
                연결할 수 있는 진행 중 요청이 없습니다.<br />
                <span style={{ fontSize: 11.5 }}>‘대기’ 요청은 먼저 접수해 주세요.</span>
              </div>
            )}
          </div>
        </div>
      ))}

      {/* ── 말소증 뷰어 — 원본 이미지/PDF 확대 ── */}
      {viewer && (
        <div role="dialog" aria-label="말소증 보기" onClick={closeViewer}
          style={{ position: 'fixed', inset: 0, zIndex: 1300, background: 'rgba(15,23,42,.72)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: isMobile ? 8 : 24 }}>
          <div onClick={function(e) { e.stopPropagation(); }}
            style={{ background: '#fff', borderRadius: 12, width: 'min(940px, 96vw)', maxHeight: '92vh',
              display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderBottom: '1px solid #e2e8f0', flexWrap: 'wrap' }}>
              <b style={{ fontSize: 14, color: '#1B3A5C' }}>{viewer.item.carNo || '(차량번호 없음)'}</b>
              <span style={muted}>{viewer.item.carType || ''}</span>
              <span style={muted}>말소일 {viewer.item.date || '-'}</span>
              {viewer.item.district ? <span style={muted}>{viewer.item.district}</span> : null}
              {viewer.item.owner ? <span style={muted}>소유자 {viewer.item.owner}</span> : null}
              <span style={muted}>{viewer.item.subtotal ? fmt(viewer.item.subtotal) + '원' : ''}</span>
              <div style={{ flex: 1 }} />
              <button className="btn btn-outline btn-sm" style={tapBtn} onClick={viewerNewTab} disabled={!viewer.url}>새 탭에서 열기</button>
              <button className="btn btn-outline btn-sm" style={tapBtn} aria-label="닫기" onClick={closeViewer}>✕ 닫기</button>
            </div>
            <div style={{ flex: 1, minHeight: 0, overflow: 'auto', background: '#0f172a1a', display: 'flex', alignItems: 'flex-start', justifyContent: 'center' }}>
              {viewer.loading
                ? <div style={{ padding: 60, color: '#64748b' }}>불러오는 중…</div>
                : viewer.isPdf
                  ? <iframe title="말소증 PDF" src={viewer.url} style={{ width: '100%', height: isMobile ? '70vh' : '78vh', border: 'none', background: '#fff' }} />
                  : <img src={viewer.url} alt={'말소증 ' + (viewer.item.carNo || '')} style={{ maxWidth: '100%', height: 'auto', display: 'block' }} />}
            </div>
          </div>
        </div>
      )}

      {/* ── 스캔 모달 ── */}
      {/* 파일만 고르는 모달 — 거래처 선택 없음, [등록 시작] 하면 바로 닫히고 진행은 위 스트립/결과 패널이 맡는다 */}
      {scanOpen && sheet(isMobile, '말소증 등록', closeScan, (
        <div>
          {/* 브라우저 기본 '파일 선택' 라벨은 못 바꾼다 — input 은 숨기고 버튼이 대신 연다 (aria 는 input 유지) */}
          <input id="malso-scan-file" type="file" multiple accept="image/*,application/pdf" aria-label="말소증 파일"
            style={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }}
            onChange={function(e) { setScanFiles(Array.prototype.slice.call(e.target.files || [])); }} />
          <button className={scanFiles.length ? 'btn btn-outline' : 'btn btn-blue'}
            style={Object.assign({ fontWeight: 700 }, tapBtn)}
            onClick={function() { var el = document.getElementById('malso-scan-file'); if (el) el.click(); }}>
            {scanFiles.length ? '↺ 파일 재선택' : '파일 선택'}</button>
          <p style={{ margin: '10px 0 0', fontSize: 11.5, color: '#94a3b8', lineHeight: 1.5 }}>
            업로드만 먼저 끝내고 인식은 뒤에서 자동으로 진행됩니다. 진행 중인 말소 요청과 차량번호가 맞으면 그 요청이 자동으로 완료됩니다.
          </p>
          {scanFiles.length > 0 && (
            <div aria-label="선택한 파일" style={{ marginTop: 12, border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
              <div style={{ padding: '7px 11px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', fontSize: 12.5, fontWeight: 800, color: '#1B3A5C' }}>
                {scanFiles.length}개 파일 선택됨
              </div>
              <div style={{ maxHeight: 190, overflowY: 'auto' }}>
                {scanFiles.map(function(f, i) {
                  return (<div key={i} style={{ display: 'flex', gap: 8, padding: '5px 11px', borderBottom: '1px solid #f8fafc', fontSize: 12, color: '#475569' }}>
                    <span style={{ color: '#cbd5e1', fontWeight: 700, width: 18, textAlign: 'right' }}>{i + 1}</span>
                    <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                  </div>);
                })}
              </div>
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <button className="btn btn-blue" style={Object.assign({ flex: 1, justifyContent: 'center' }, tapBtn)}
              disabled={scanFiles.length === 0} onClick={startScan}>등록 시작</button>
            <button className="btn btn-outline" style={Object.assign({ justifyContent: 'center', color: '#94a3b8' }, tapBtn)}
              onClick={closeScan}>닫기</button>
          </div>
        </div>
      ))}
    </div>
  );
}

var UNFILED = { key: 'unassigned', name: '미분류' };
var RECENT = { key: 'recent', name: '신규 등록' };   // 오늘(KST) 등록분 — 서버 folderItems folder=recent
// 패널 높이 하한 — 이보다 낮은 창에서는 채우기를 포기하고 셸이 스크롤한다 (내용이 뭉개지는 것보다 낫다)
var PANEL_MIN = 460;
var PANEL_GAP = 16;   // 패널 바닥과 화면(스크롤 영역) 바닥 사이 숨통
var CARD = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden', padding: 0 };
var FOLDER = { display: 'flex', alignItems: 'center', gap: 7, padding: '8px 9px', borderRadius: 8, marginBottom: 2,
  fontSize: 12.5, color: '#334155', cursor: 'pointer',
  borderWidth: 1, borderStyle: 'solid', borderColor: 'transparent' };   // shorthand 금지 — on 상태가 borderColor 만 덮는다
var FCHIP = { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '6px 11px', borderRadius: 999,
  borderWidth: 1, borderStyle: 'solid', borderColor: '#e2e8f0',
  background: '#fff', color: '#475569', fontSize: 12, fontWeight: 700,
  whiteSpace: 'nowrap', cursor: 'pointer', fontFamily: 'Pretendard,sans-serif' };
var FCHIP_TAP = { padding: '10px 13px', fontSize: 12.5 };   // 모바일 폴더 칩 — 높이 ≈ 37px
var CHIP = { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 9px', borderRadius: 999,
  border: '1px solid #e2e8f0', background: '#f8fafc', fontSize: 11.5, fontWeight: 700, color: '#475569' };
var CHIP_OK = { background: '#ecfdf5', borderColor: '#a7f3d0', color: '#047857' };
var CHIP_BLUE = { background: '#eff6ff', borderColor: '#bfdbfe', color: '#1d4ed8' };
var CHIP_WARN = { background: '#fffbeb', borderColor: '#fcd34d', color: '#92400e' };
var CHIP_DAN = { background: '#fef2f2', borderColor: '#fecaca', color: '#b91c1c' };
var BDG = { display: 'inline-block', padding: '1px 6px', borderRadius: 5, fontSize: 10, fontWeight: 800 };
var BDG_WARN = { background: '#fffbeb', color: '#92400e', border: '1px solid #fcd34d' };
var BDG_BLUE = { background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe' };
var BDG_GRAY = { background: '#f1f5f9', color: '#64748b', border: '1px solid #e2e8f0' };
var BDG_NEW = { background: '#2563EB', color: '#fff', border: '1px solid #2563EB', letterSpacing: 0.2 };
var BDG_DAN = { background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca' };

function kpiCell(label, value, amber, isMobile) {
  return (<div key={label} style={{ textAlign: isMobile ? 'center' : 'right', lineHeight: 1.25 }}>
    <span style={{ display: 'block', fontSize: 10.5, color: '#94a3b8', fontWeight: 600 }}>{label}</span>
    <strong style={{ fontSize: 15, fontWeight: 800, color: amber ? '#b45309' : '#1B3A5C' }}>{value}</strong>
  </div>);
}

// 화면 폭 3단 — ≥1280 desktop(3-pane) / ≥768 tablet(2-pane) / 그 외 mobile(스택).
// App.css 셸의 768px 브레이크포인트와 하단 경계를 맞춘다 (useMobile 훅과 동일 기준).
function useWidthTier() {
  var read = function() {
    var w = (typeof window !== 'undefined' && window.innerWidth) || 1280;
    return w >= 1280 ? 'desktop' : (w >= 768 ? 'tablet' : 'mobile');
  };
  var st = useState(read);
  var tier = st[0], setTier = st[1];
  useEffect(function() {
    if (typeof window === 'undefined') return undefined;
    var on = function() { setTier(read()); };
    window.addEventListener('resize', on);
    on();
    return function() { window.removeEventListener('resize', on); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return tier;
}

// KST 기준 이번 달 'YYYY-MM' (서버 utils/malsoBox.monthKst 와 같은 계산 — 자정 경계 월 어긋남 방지)
function kstMonth() {
  return new Date(Date.now() + 9 * 3600 * 1000).toISOString().slice(0, 7);
}
function shiftMonth(m, d) {
  var y = parseInt(String(m).slice(0, 4), 10);
  var mo = parseInt(String(m).slice(5, 7), 10) + d;
  y += Math.floor((mo - 1) / 12);
  mo = ((mo - 1) % 12 + 12) % 12 + 1;
  return y + '-' + (mo < 10 ? '0' : '') + mo;
}

// 모달/시트 — 모바일은 바텀시트, 데스크탑은 가운데 모달 (CancelAgencyClients editSheet 관례)
function sheet(isMobile, title, onClose, body) {
  return (<div role="dialog" aria-label={title}
    style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.45)', zIndex: 9998, display: 'flex', flexDirection: 'column', justifyContent: isMobile ? 'flex-end' : 'center', alignItems: isMobile ? undefined : 'center' }}
    onClick={onClose}>
    <div onClick={function(e) { e.stopPropagation(); }}
      style={{ background: '#fff', borderRadius: isMobile ? '16px 16px 0 0' : 12, width: isMobile ? undefined : 420, maxWidth: isMobile ? undefined : '94vw', maxHeight: '92vh', overflowY: 'auto', padding: isMobile ? '14px 14px 28px' : 20, fontFamily: 'Pretendard,sans-serif' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <div style={{ flex: 1, fontSize: 15, fontWeight: 800, color: '#1B3A5C' }}>{title}</div>
        <button onClick={onClose} aria-label="닫기" style={{ border: 'none', background: 'none', fontSize: 18, color: '#94a3b8', minWidth: 40, minHeight: 40, cursor: 'pointer', padding: 0 }}>✕</button>
      </div>
      {body}
    </div>
  </div>);
}

export default CancelAgencyMalsoBox;
